/**
 * SharePoint API service — connects to the Express backend.
 *
 * Backend routes:
 *   GET    /sharepoint/folders?parent_id=...
 *   POST   /sharepoint/folders
 *   DELETE  /sharepoint/folders/:id
 *   POST   /sharepoint/files/generate-url
 *   POST   /sharepoint/files/confirm
 *   GET    /sharepoint/files/view/:id
 *   DELETE  /sharepoint/files/:id
 *   GET    /sharepoint/breadcrumb/:folderId
 *
 * Companies (existing endpoint):
 *   GET    /company/dropdown
 */

import axios from "axios";
import api from "@/shared/services/apiClient";
import type {
  SharepointFolder,
  SharepointFile,
  BreadcrumbItem,
  StorageQuota,
} from "@/features/files/types/sharepoint";

// =====================================================================
// FOLDERS
// =====================================================================

/**
 * Fetch folders (and files if inside a folder).
 * The backend handles RBAC — officers see all; trainees see company-scoped.
 */
export async function fetchFolderContents(
  parentId: string | null,
): Promise<{ folders: SharepointFolder[]; files: SharepointFile[] }> {
  const params: Record<string, string> = {};
  if (parentId) params.parent_id = parentId;

  const { data } = await api.get("/sharepoint/folders", { params });
  return data; // { folders, files }
}

/**
 * Create a new folder.
 */
export async function createFolder(
  name: string,
  parentId: string | null,
  companyIds: string[],
): Promise<SharepointFolder> {
  const { data } = await api.post("/sharepoint/folders", {
    name,
    parent_id: parentId,
    company_ids: companyIds,
  });
  return data;
}

/**
 * Update a folder's name and/or company_ids (officer only).
 */
export async function updateFolder(
  folderId: string,
  payload: { name?: string; company_ids?: string[] },
): Promise<SharepointFolder> {
  const { data } = await api.patch(`/sharepoint/folders/${folderId}`, payload);
  return data;
}

/**
 * Delete a folder and all its children (officer only).
 */
export async function deleteFolder(folderId: string): Promise<void> {
  await api.delete(`/sharepoint/folders/${folderId}`);
}

// =====================================================================
// FILES
// =====================================================================

/**
 * Three-step presigned-URL upload flow:
 * 1. Get presigned URL from backend
 * 2. PUT file directly to S3
 * 3. Confirm upload in backend DB
 */
export async function uploadFile(
  file: File,
  folderId: string,
  onProgress?: (pct: number) => void,
): Promise<SharepointFile> {
  // Step 1: Get presigned upload URL
  const { data: urlData } = await api.post("/sharepoint/files/generate-url", {
    fileName: file.name,
    fileType: file.type,
    folderId,
  });

  const { uploadUrl, s3Key, bucketName } = urlData;

  // Step 2: Upload directly to S3
  await axios.put(uploadUrl, file, {
    headers: { "Content-Type": file.type },
    onUploadProgress: (progressEvent) => {
      if (onProgress && progressEvent.total) {
        const pct = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        onProgress(pct);
      }
    },
  });

  // Step 3: Confirm in DB
  const { data: fileRecord } = await api.post("/sharepoint/files/confirm", {
    folder_id: folderId,
    display_name: file.name,
    s3_key: s3Key,
    s3_bucket: bucketName,
    file_type: file.type,
    file_size: file.size,
  });

  return fileRecord;
}

/**
 * Get a temporary presigned viewing URL for a file.
 */
export async function getFileViewUrl(fileId: string): Promise<string> {
  const { data } = await api.get(`/sharepoint/files/view/${fileId}`);
  return data.url;
}

/**
 * Rename a file (officer only).
 */
export async function updateFile(
  fileId: string,
  payload: { display_name: string },
): Promise<SharepointFile> {
  const { data } = await api.patch(`/sharepoint/files/${fileId}`, payload);
  return data;
}

/**
 * Delete a file (officer only).
 */
export async function deleteFile(fileId: string): Promise<void> {
  await api.delete(`/sharepoint/files/${fileId}`);
}

// =====================================================================
// BREADCRUMB
// =====================================================================

/**
 * Build breadcrumb trail from root to a given folder.
 * Returns [root segment, ...ancestor segments].
 */
export async function getBreadcrumb(
  folderId: string | null,
): Promise<BreadcrumbItem[]> {
  const root: BreadcrumbItem = { id: null, name: "Files" };
  if (!folderId) return [root];

  const { data } = await api.get(`/sharepoint/breadcrumb/${folderId}`);
  // Backend returns { breadcrumb: [{ id, name }, ...] } from root to current
  const segments: BreadcrumbItem[] = (data.breadcrumb || []).map(
    (item: { id: string; name: string }) => ({
      id: item.id,
      name: item.name,
    }),
  );

  return [root, ...segments];
}

// =====================================================================
// STORAGE QUOTA
// =====================================================================

export async function getStorageQuota(): Promise<StorageQuota> {
  const { data } = await api.get("/sharepoint/storage-quota");
  return data;
}

// =====================================================================
// COMPANIES (uses existing /company endpoint)
// =====================================================================

/**
 * Fetch companies for the officer's business unit.
 * Uses the existing /company/dropdown endpoint which returns
 * [{ value: "company_id", label: "Company Name" }, ...]
 */
export async function fetchCompanies(): Promise<
  { id: string; name: string }[]
> {
  const { data } = await api.get("/company/dropdown");
  // Map from { value, label } to { id, name }
  return (data || []).map((c: { value: string; label: string }) => ({
    id: c.value,
    name: c.label,
  }));
}
